import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, ShieldCheck } from "lucide-react";
import { api, LOGO_MARK, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function PayInvoice() {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: params.get("name") || "",
    customer_email: params.get("email") || "",
    description: params.get("desc") || params.get("for") || "",
    amount: params.get("amount") || "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pay = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!form.customer_name.trim()) return toast.error("Please enter your name.");
    if (!form.customer_email.trim()) return toast.error("Please enter your email.");
    if (!form.description.trim()) return toast.error("Please add what this payment is for.");
    if (!amt || amt < 1 || amt > 50000) return toast.error("Enter a valid amount between $1 and $50,000.");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/invoice", {
        amount: amt,
        description: form.description.trim(),
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const preview = parseFloat(form.amount);

  return (
    <div className="grain relative min-h-screen flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full" data-testid="pay-invoice-page">
        <div className="text-center mb-8">
          <img src={LOGO_MARK} alt="E.O.VO" className="h-14 w-14 object-contain mx-auto mb-5" />
          <h1 className="font-head text-3xl md:text-4xl font-bold">Pay Your Invoice</h1>
          <p className="text-[#A1A1AA] mt-3 text-sm leading-relaxed">
            Enter the amount agreed with Evan for your project and pay securely. You'll receive a receipt by email.
          </p>
        </div>

        <form onSubmit={pay} className="rounded-xl border border-white/10 bg-[#12141A] p-7 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-[#A1A1AA]">Your name</Label>
              <Input value={form.customer_name} onChange={set("customer_name")} placeholder="Jane Doe"
                data-testid="invoice-name" className="bg-[#0B0C10] border-white/10 mt-1.5" />
            </div>
            <div>
              <Label className="text-xs text-[#A1A1AA]">Email</Label>
              <Input type="email" value={form.customer_email} onChange={set("customer_email")} placeholder="jane@studio.com"
                data-testid="invoice-email" className="bg-[#0B0C10] border-white/10 mt-1.5" />
            </div>
          </div>

          <div>
            <Label className="text-xs text-[#A1A1AA]">What's this payment for?</Label>
            <Textarea value={form.description} onChange={set("description")} rows={2}
              placeholder="e.g. Audiobook narration — 45,000 words (final)"
              data-testid="invoice-description" className="bg-[#0B0C10] border-white/10 mt-1.5 resize-none" />
          </div>

          <div>
            <Label className="text-xs text-[#A1A1AA]">Amount (USD)</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C8A97E] font-mono text-lg">$</span>
              <Input type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} placeholder="250.00"
                data-testid="invoice-amount" className="bg-[#0B0C10] border-white/10 pl-8 text-lg font-mono" />
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-white/10 pt-4">
            <span className="text-[#A1A1AA] text-sm">Total due today</span>
            <span className="font-head text-3xl font-bold text-gradient-gold" data-testid="invoice-total">
              ${!isNaN(preview) && preview > 0 ? preview.toFixed(2) : "0.00"}
            </span>
          </div>

          <Button type="submit" disabled={loading} data-testid="invoice-pay"
            className="w-full bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm h-12 text-base">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <><Lock size={16} className="mr-2" /> Pay Securely</>}
          </Button>
          <p className="text-[11px] text-center text-[#A1A1AA] flex items-center justify-center gap-1.5">
            <ShieldCheck size={13} className="text-[#C8A97E]" /> Secure checkout via Stripe · Test card: 4242 4242 4242 4242
          </p>
        </form>

        <Link to="/" data-testid="pay-back-home" className="flex items-center justify-center gap-2 mt-7 text-sm text-[#C8A97E] hover:underline">
          <ArrowLeft size={15} /> Back to home
        </Link>
      </div>
    </div>
  );
}
